import { devtoolsExchange } from "@urql/devtools";
import { authExchange } from "@urql/exchange-auth";
// TODO: use normalized cache once hasura includes remote schema merging (See: https://github.com/hasura/graphql-engine/issues/5801)
// import { offlineExchange } from "@urql/exchange-graphcache";
import { retryExchange } from "@urql/exchange-retry";
import type { JwtPayload } from "jwt-decode";
import jwtDecode from "jwt-decode";
// import type { IntrospectionQuery } from "graphql";
import * as React from "react";
// import { SubscriptionClient } from "subscriptions-transport-ws";
import type { Client } from "urql";
import {
  createClient,
  debugExchange,
  fetchExchange,
  gql,
  makeOperation,
  Provider,
  // subscriptionExchange,
} from "urql";
import type { Cache } from "~/cache/graphcache/src";
import { offlineExchange } from "~/cache/graphcache/src";
import { authFlowController } from "~/config/auth0/auth-flow-controller";
import { browserState } from "~/config/auth0/browser-state";
import { useAuth0 } from "~/config/auth0/use-auth0";
import { env } from "~/config/env";
import type { CreateTaskInput } from "~/gql/graphql";
import {
  ProjectTasksList_DataDocument,
  TaskEdit_DataDocument,
} from "~/gql/graphql";
import { FullPageSpinner } from "~/screens/App/components/FullPageSpinner/FullPageSpinner";
import type { ProjectTasksList_DataQuery } from "~/screens/App/screens/Project/Tasks/screens/ProjectTasksListScreen/components/ProjectTasksList/ProjectTasksList.graphql";
import type { TaskItem } from "~/screens/App/screens/Project/Tasks/screens/ProjectTasksListScreen/components/ProjectTasksList/ProjectTasksList.types";
import { currentNetworkOnlineStatus } from "../capacitor/network/hook";
import { captureException } from "../sentry";
import {
  generateTemporaryTaskId,
  getStoredVariables,
  handleCreateTaskOptimisticUpdate,
} from "./helpers";
import { updateTaskValidationInCache } from "./offlineValidation.helpers";
import { makeIndexedDbStorage, makeLocalStorage } from "./storages";
import { visitSyncCacheKeys } from "./visit-sync-cache";

declare global {
  interface Window {
    __DEBUG_URQL__?: boolean;
    __hasCacheRehydrated?: boolean;
  }
}

interface UrqlConfigProps {
  children: React.ReactNode;
}

// Enable debug mode to debug urql cache
const debug = false; //!!env.DEV;

const DEBUG_URQL =
  (typeof window !== "undefined" && window.__DEBUG_URQL__ === true) ||
  (typeof localStorage !== "undefined" &&
    localStorage.getItem("DEBUG_URQL") === "1");

const getStorage = () => {
  return env.DEV ? makeLocalStorage() : makeIndexedDbStorage();
};

const compositeCacheKey = (...values: unknown[]): string | null =>
  values.every((value) => typeof value === "string")
    ? (values as string[]).join(":")
    : null;

export const extractQueryFromErrorMessage = (
  str: string
): string | undefined => {
  if (str.includes("The GraphQL query at the field at")) {
    // Use a regular expression to match the content between backticks
    const match = str.match(/`(.*?)`/);
    const query = match?.[1];
    return query;
  } else if (str.includes('No value for field "id" on entity')) {
    const match = str.match(/entity "(.*)"/);
    const query = match?.[1];
    return query;
  }

  return;
};

const isRootQueryProblem = (query: string): boolean => {
  let numberOfDotsToMatch = 1;
  // if end with digits it's an array so, we can have  one more dots
  if (/\d$/.test(query)) {
    numberOfDotsToMatch++;
  }

  const numberOfDots = query.split(".").length - 1;
  return numberOfDots === numberOfDotsToMatch;
};

const invalidateCache = (cache: Cache, types: string[] = []) => {
  types.forEach((type) => {
    cache.invalidate(type);
  });
};

const offlineCache = offlineExchange({
  debug,
  // We can't cache aggregate queries because they don't have a key
  // To avoid error to be display, we should add below
  keys: {
    ...visitSyncCacheKeys,
    /**
     * Bad remote schema
     */
    GetOrgProejectTemplate: () => null,

    // should be composed index
    orgs_to_users: () => null,

    project_roles: () => null,

    /**
     * 
     * 
    // no index key
    */
    projects_count: () => null,

    projects_map_overview_aggregate: () => null,

    projects_to_users: () => null,

    tasks_aggregate: () => null,

    // why no id?
    user_avatar: () => null,

    user_avatars: () => null,

    visit_document_buildings: (data) =>
      compositeCacheKey(data.visit_id, data.file_id, data.project_building_id),

    visit_document_floors: (data) =>
      compositeCacheKey(data.visit_id, data.file_id, data.project_floor_id),
    visit_documents: (data) => compositeCacheKey(data.visit_id, data.file_id),
  },
  // If we have some cache error, cache could be corrupted and return null
  // So we use console.error to log the error
  // https://github.com/urql-graphql/urql/issues/3526
  // Our custom logger prevent developper if there is no ID at the root query
  logger: (severity, message) => {
    const query = extractQueryFromErrorMessage(message);

    const isQuery = (str: string) =>
      str.startsWith("Query") || str.startsWith("query_root");
    if (query) {
      if (isQuery(query) && isRootQueryProblem(query)) {
        console.error(query, {
          causedBy: message.split("Caused At")?.[1],
          message,
          query,
          type: "urql:cache:root",
        });
      } else {
        if (debug) {
          console.warn(query, {
            message,
            query,
            type: "urql:cache:children",
          });
        }
      }
    } else {
      /*
      console.info(message, {
        message,
        type: "urql:cache:other",
      });
      */
    }
  },

  /**
   * Check the documentation about offline v1.3 findings and discoveries
   * @see https://app.gitbook.com/o/-LlhZY09V4VTR7cy_55t/s/-MSitZZVNivUi5eDfPNe/guidelines/frontend/offline-task-management-v1.3-feature-overview
   */
  optimistic: {
    createTask: () => {
      const isOffline = !currentNetworkOnlineStatus;

      if (!isOffline) return null;

      const taskId = generateTemporaryTaskId(true);

      return {
        __typename: "CreateTaskSuccess",
        task_id: taskId,
      };
    },

    deleteTask: (args) => {
      const argsObj = { ...args } as { input: { id: string } };
      const taskId = argsObj.input.id;

      return {
        __typename: "DeleteTaskSuccess",
        task_id: taskId,
      };
    },

    updateSubtask: (args) => {
      const argsObj = { ...args } as { input: { subtaskId: string } };
      const subtaskId = argsObj.input.subtaskId;

      return {
        __typename: "UpdateSubtaskSuccess",
        subtask_id: subtaskId,
      };
    },

    updateTask: (args) => {
      const argsObj = { ...args } as { input: { id: string } };
      const taskId = argsObj.input.id;

      return {
        __typename: "UpdateTaskSuccess",
        task_id: taskId,
      };
    },

    updateUserProfile: (args, cache, info) => {
      const variables = info.variables;
      const userId = variables.userId;

      return {
        __typename: "UpdateUserProfileSuccess",
        user_id: userId,
      };
    },

    updateValidationTask: (args) => {
      const argsObj = { ...args } as { input: { taskId: string } };
      const taskId = argsObj.input.taskId;

      return {
        __typename: "UpdateValidationTaskSuccess",
        task_id: taskId,
      };
    },
  },

  // If we use schema, updates Mutation seemns not to be called,
  // so we can't invalidate the cache
  // schema: schema,
  storage: getStorage(),

  updates: {
    Mutation: {
      // We customized graphcache for having typenames
      "*": (_result, _args, cache, info) => {
        const context = info.context;
        if (DEBUG_URQL) {
          console.log("🔥🔥🔥urql:updates:Mutation:*", {
            _args,
            _result,
            cache,
            info,
          });
        }

        const additionalTypenames = context?.additionalTypenames;
        if (additionalTypenames?.length) {
          // If we do not invalidate the ui won't take changes
          if (!info.optimistic) {
            invalidateCache(cache, additionalTypenames);
          }
        }
      },

      createTask: (_result, args, cache, info) => {
        const isOffline = !currentNetworkOnlineStatus;
        if (info.optimistic && isOffline) {
          const { input } = { ...args } as { input: CreateTaskInput };
          handleCreateTaskOptimisticUpdate(input, cache);
        }
      },

      deleteTask: (_result, _args, cache, info) => {
        if (info.optimistic) {
          const taskId = (
            _result as { deleteTask?: { task_id?: string } } | null
          )?.deleteTask?.task_id;
          if (!taskId) {
            return;
          }

          const variables = getStoredVariables();

          cache.updateQuery(
            {
              query: ProjectTasksList_DataDocument,
              variables: variables,
            },
            (data) => {
              if (!data || !data.projects_by_pk || !data.projects_by_pk.tasks) {
                return data;
              }

              const filteredTasks = data.projects_by_pk.tasks.map((task) => {
                if ((task as TaskItem).id === taskId) {
                  return {
                    ...task,
                    deleted: true,
                  };
                }
                return task;
              });

              const updatedData = {
                ...data,
                projects_by_pk: {
                  ...data.projects_by_pk,
                  tasks: filteredTasks,
                },
              };

              return updatedData;
            }
          );
        }
      },

      updateSubtask: (_result, _args, cache, info) => {
        if (info.optimistic) {
          const subtaskId = (_result?.updateSubtask as { subtask_id: string })
            .subtask_id;
          const input = _args.input as {
            closed: boolean;
            description: string;
            endDate: string | null;
            startDate: string | null;
            subtaskId: string;
          };

          const variables = getStoredVariables();

          const projectId = variables.projectId;

          const queries = cache.inspectFields("Query");

          const taskQueries = queries.filter((query) => {
            const isTaskQuery =
              query.fieldName === "tasks" ||
              query.fieldName === "tasks_by_pk" ||
              query.fieldName === "projects_by_pk";

            return isTaskQuery;
          });

          for (const query of taskQueries) {
            const queryArgs = query.arguments ?? {};

            if (!projectId) {
              if (DEBUG_URQL) {
                console.log(
                  "🔥 updateSubtask: Skipping tasks query - no projectId.",
                  {
                    fieldKey: query.fieldKey,
                    queryArgs,
                  }
                );
              }
              continue;
            }

            // Update tasks query
            if (query.fieldName === "tasks") {
              cache.updateQuery(
                {
                  query: ProjectTasksList_DataDocument,
                  //@ts-expect-error – todo: fix this
                  variables: { ...queryArgs, projectId },
                },
                (data: ProjectTasksList_DataQuery) => {
                  if (!data || typeof data !== "object" || !("tasks" in data)) {
                    if (DEBUG_URQL) {
                      console.log(
                        "🔥 updateSubtask: No data found for tasks query",
                        {
                          fieldKey: query.fieldKey,
                          queryArgs,
                        }
                      );
                    }
                    return data;
                  }

                  const typedData = data as {
                    tasks: Array<{ subtasks?: Array<{ id: string }> }>;
                  };
                  if (!typedData.tasks) {
                    if (DEBUG_URQL) {
                      console.log("🔥 updateSubtask: No tasks found in data", {
                        data,
                        fieldKey: query.fieldKey,
                        queryArgs,
                      });
                    }
                    return data;
                  }

                  const updatedTasks = typedData.tasks.map((task) => {
                    if (!task.subtasks) return task;

                    const updatedSubtasks = task.subtasks.map((subtask) => {
                      if (subtask.id === subtaskId) {
                        return {
                          ...subtask,
                          closed: input.closed,
                          description: input.description,
                          end_date: input.endDate,
                          start_date: input.startDate,
                        };
                      }
                      return subtask;
                    });

                    return {
                      ...task,
                      subtasks: updatedSubtasks,
                    };
                  });

                  return {
                    ...data,
                    tasks: updatedTasks,
                  };
                }
              );
            }

            if (query.fieldName === "projects_by_pk") {
              cache.updateQuery(
                {
                  query: ProjectTasksList_DataDocument,
                  //@ts-expect-error – todo: fix this
                  variables: { ...queryArgs, projectId },
                },
                (data: unknown) => {
                  if (
                    !data ||
                    typeof data !== "object" ||
                    !("projects_by_pk" in data)
                  ) {
                    if (DEBUG_URQL) {
                      console.log(
                        "🔥 updateSubtask: No data found for projects_by_pk query",
                        {
                          fieldKey: query.fieldKey,
                          queryArgs,
                        }
                      );
                    }
                    return data;
                  }

                  const typedData = data as {
                    projects_by_pk: {
                      tasks?: Array<{ subtasks?: Array<{ id: string }> }>;
                    };
                  };
                  if (!typedData.projects_by_pk?.tasks) {
                    if (DEBUG_URQL) {
                      console.log(
                        "🔥 updateSubtask: No tasks found in projects_by_pk data",
                        {
                          data,
                          fieldKey: query.fieldKey,
                          queryArgs,
                        }
                      );
                    }
                    return data;
                  }

                  const updatedTasks = typedData.projects_by_pk.tasks.map(
                    (task) => {
                      if (!task.subtasks) return task;

                      const updatedSubtasks = task.subtasks.map((subtask) => {
                        if (subtask.id === subtaskId) {
                          return {
                            ...subtask,
                            closed: input.closed,
                            description: input.description,
                            end_date: input.endDate,
                            start_date: input.startDate,
                          };
                        }
                        return subtask;
                      });

                      return {
                        ...task,
                        subtasks: updatedSubtasks,
                      };
                    }
                  );

                  return {
                    ...data,
                    projects_by_pk: {
                      ...typedData.projects_by_pk,
                      tasks: updatedTasks,
                    },
                  };
                }
              );
            }

            if (query.fieldName === "tasks_by_pk") {
              const taskId = (query.arguments as { id: string })?.id;
              if (!taskId) continue;

              cache.updateQuery(
                {
                  query: TaskEdit_DataDocument,
                  variables: { projectId, taskId },
                },
                (data: unknown) => {
                  if (!data || typeof data !== "object" || !("task" in data)) {
                    if (DEBUG_URQL) {
                      console.log(
                        "🔥 updateSubtask: No data found for tasks_by_pk query",
                        {
                          fieldKey: query.fieldKey,
                          queryArgs,
                        }
                      );
                    }
                    return data;
                  }

                  const typedData = data as {
                    task: { subtasks?: Array<{ id: string }> };
                  };
                  if (!typedData.task) {
                    if (DEBUG_URQL) {
                      console.log("🔥 updateSubtask: No task found in data", {
                        data,
                        fieldKey: query.fieldKey,
                        queryArgs,
                      });
                    }
                    return data;
                  }

                  if (!typedData.task.subtasks) {
                    if (DEBUG_URQL) {
                      console.log(
                        "🔥 updateSubtask: No subtasks found in task",
                        {
                          data,
                          fieldKey: query.fieldKey,
                          queryArgs,
                        }
                      );
                    }
                    return data;
                  }

                  const updatedSubtasks = typedData.task.subtasks.map(
                    (subtask) => {
                      if (subtask.id === subtaskId) {
                        return {
                          ...subtask,
                          closed: input.closed,
                          description: input.description,
                          end_date: input.endDate,
                          start_date: input.startDate,
                        };
                      }
                      return subtask;
                    }
                  );

                  return {
                    ...data,
                    task: {
                      ...typedData.task,
                      subtasks: updatedSubtasks,
                    },
                  };
                }
              );
            }
          }
        }
      },

      updateTask: (_result, _args, cache, info) => {
        const taskId = (_result?.updateTask as { task_id: string }).task_id;
        const input = _args.input as {
          id: string;
          start_date?: string | null;
          end_date?: string | null;
          description?: string;
          [key: string]: unknown;
        };

        // Check if this is a date update (Gantt drag & drop)
        const isDateUpdate =
          input &&
          (input.start_date !== undefined || input.end_date !== undefined);

        if (info.optimistic) {
          const variables = getStoredVariables();

          cache.updateQuery(
            {
              query: ProjectTasksList_DataDocument,
              variables: variables,
            },
            (data) => {
              if (!data || !data.projects_by_pk || !data.projects_by_pk.tasks) {
                return data;
              }

              const updatedTasks = data.projects_by_pk.tasks.map((task) => {
                if ((task as TaskItem).id === taskId) {
                  return { ...task, ...(input as object) };
                }
                return task;
              });

              return {
                ...data,
                projects_by_pk: {
                  ...data.projects_by_pk,
                  tasks: updatedTasks,
                },
              };
            }
          );
        }

        // For date updates on real server response, also invalidate cache
        if (isDateUpdate && !info.optimistic) {
          invalidateCache(cache, ["tasks", "projects_by_pk"]);
        }
      },

      updateUserProfile: (_result, _args, cache, info) => {
        console.log("🔥 updateUserProfile mutation update", {
          _args,
          _result,
          cache,
          info,
        });

        if (info.optimistic) {
          const userId = (
            _result as { updateUserProfile?: { user_id?: string } } | null
          )?.updateUserProfile?.user_id;
          if (!userId) {
            return;
          }

          const input = _args.input as Record<string, unknown>;

          const fragment = gql`
            fragment _ on users {
              id
              country_code
              first_name
              language
              last_name
              phone
            }
          `;

          cache.writeFragment(fragment, {
            ...input,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            id: userId,
          });
        }
      },

      updateValidationTask: (_result, _args, cache, info) => {
        if (info.optimistic) {
          const taskId = (_result?.updateValidationTask as { task_id: string })
            ?.task_id;

          const variables = getStoredVariables();
          const projectId = variables.projectId;

          updateTaskValidationInCache(cache, taskId, projectId);
        }
      },
    },
  },
});

const NON_TRANSIENT_AUTH_ERROR_MARKERS = [
  "access_denied",
  "consent_required",
  "interaction_required",
  "invalid_grant",
  "invalid_refresh_token",
  "invalid_token",
] as const;

const EXPECTED_UNAUTHENTICATED_AUTH_ERROR_MARKERS = [
  "login_required",
  "login required",
  "missing_refresh_token",
  "missing refresh token",
] as const;

const TRANSIENT_AUTH_ERROR_MARKERS = [
  "503",
  "504",
  "econn",
  "failed to fetch",
  "network error",
  "net::err_",
  "is offline",
  "went offline",
  "timed out",
  "timeout",
  "temporarily unavailable",
] as const;

const AUTH_SESSION_ERROR_MARKERS = [
  "expired",
  "invalid_grant",
  "invalid_refresh_token",
  "invalid_token",
] as const;

type AuthErrorShape = {
  cause?: unknown;
  error?: unknown;
  error_description?: unknown;
  message?: unknown;
  name?: unknown;
};

const createAuthRefreshError = (error: unknown): Error => {
  if (error instanceof Error) {
    return error;
  }

  return new Error("Auth token/session expired while refreshing access token");
};

const hasAuthorizationHeader = (headers: unknown): boolean => {
  if (!headers) {
    return false;
  }

  if (headers instanceof Headers) {
    return headers.has("authorization");
  }

  if (Array.isArray(headers)) {
    return headers.some(
      (header) =>
        Array.isArray(header) &&
        typeof header[0] === "string" &&
        header[0].toLowerCase() === "authorization"
    );
  }

  if (typeof headers === "object") {
    return Object.keys(headers).some(
      (key) => key.toLowerCase() === "authorization"
    );
  }

  return false;
};

const normalizeAuthErrorText = (error: unknown): string => {
  if (error instanceof Error) {
    // Auth0 SDK errors extend Error but also have .error and .error_description
    // (e.g. error.error = "invalid_grant", error.error_description = "Unknown or invalid refresh token.")
    // We must read these Auth0-specific fields too, not just name/message.
    const authError = error as AuthErrorShape;
    const parts = [
      error.name,
      error.message,
      // Auth0-specific fields (may not exist on regular Error instances)
      typeof authError.error === "string" ? authError.error : undefined,
      typeof authError.error_description === "string"
        ? authError.error_description
        : undefined,
    ]
      .filter((part): part is string => !!part)
      .map((part) => part.toLowerCase());

    // Safely access cause property (not available on Error before ES2022)
    const errWithCause = error as { cause?: unknown };
    if (typeof errWithCause.cause !== "undefined") {
      parts.push(normalizeAuthErrorText(errWithCause.cause));
    }

    return parts.join(" ");
  }

  if (typeof error === "string") {
    return error.toLowerCase();
  }

  if (error && typeof error === "object") {
    const authError = error as AuthErrorShape;
    const parts = [
      authError.name,
      authError.error,
      authError.error_description,
      authError.message,
    ]
      .filter((part): part is string => typeof part === "string")
      .map((part) => part.toLowerCase());

    if (authError.cause) {
      parts.push(normalizeAuthErrorText(authError.cause));
    }

    return parts.join(" ");
  }

  return String(error).toLowerCase();
};

const shouldForceLogoutForAuthError = (error: unknown): boolean => {
  // Never force logout while offline; connectivity errors are recoverable.
  if (!currentNetworkOnlineStatus) {
    return false;
  }

  const normalizedError = normalizeAuthErrorText(error);

  // These errors are expected when the user is not logged in yet
  // (fresh install, cleared app storage, no persisted session).
  // Forcing /v2/logout here can create native browser open/close loops.
  if (
    EXPECTED_UNAUTHENTICATED_AUTH_ERROR_MARKERS.some((marker) =>
      normalizedError.includes(marker)
    )
  ) {
    return false;
  }

  if (
    NON_TRANSIENT_AUTH_ERROR_MARKERS.some((marker) =>
      normalizedError.includes(marker)
    )
  ) {
    return true;
  }

  if (
    TRANSIENT_AUTH_ERROR_MARKERS.some((marker) =>
      normalizedError.includes(marker)
    )
  ) {
    return false;
  }

  // Unknown error while online: force reauth to prevent infinite spinner.
  // It's better to ask the user to re-login than to leave them stuck.
  // Transient errors are already handled above.
  return true;
};

const isAuthSessionError = (error: unknown): boolean => {
  const normalizedError = normalizeAuthErrorText(error);
  return AUTH_SESSION_ERROR_MARKERS.some((marker) =>
    normalizedError.includes(marker)
  );
};

const REAUTH_REQUEST_COOLDOWN_MS = 15000;
let reauthRequestInFlight = false;
let lastReauthRequestedAt = 0;
const REFRESH_FAILURE_COOLDOWN_MS = 30000;
let blockedAuthError: Error | null = null;
let lastRefreshFailedAt = 0;
let lastRefreshError: Error | null = null;

const resetReauthRequestCircuit = () => {
  reauthRequestInFlight = false;
  lastReauthRequestedAt = 0;
};

const isRefreshFailureCoolingDown = (now = Date.now()) =>
  lastRefreshFailedAt > 0 &&
  now - lastRefreshFailedAt < REFRESH_FAILURE_COOLDOWN_MS;

const requestReauthOnce = (source: "bootstrap" | "refreshAuth") => {
  if (browserState.getIsOpen()) {
    return;
  }

  const now = Date.now();

  if (reauthRequestInFlight) {
    return;
  }

  if (now - lastReauthRequestedAt < REAUTH_REQUEST_COOLDOWN_MS) {
    return;
  }

  reauthRequestInFlight = true;
  lastReauthRequestedAt = now;
  authFlowController.requestReauth(
    source === "bootstrap" ? "urql_bootstrap" : "urql_refresh_auth"
  );
  reauthRequestInFlight = false;
};

function useClient() {
  const { getAccessTokenSilently } = useAuth0();

  // TODO: Fix hasura subscription high cpu usage before re-enabling websocket
  // See:
  // https://github.com/hasura/graphql-engine/issues/6542
  // https://github.com/hasura/graphql-engine/issues/6042
  //
  // const subscriptionClient = new SubscriptionClient(
  //   env.VITE_HASURA_SUBSCRIPTION_URL,
  //   {
  //     async connectionParams() {
  //       const token = await getAccessTokenSilently();
  //       return {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       };
  //     },
  //     // See: https://github.com/hasura/graphql-engine/issues/4509#issuecomment-882503283
  //     inactivityTimeout: 30000,
  //     lazy: true,
  //     minTimeout: 2000,
  //     reconnect: true,
  //   }
  // );

  const client = createClient({
    exchanges: [
      ...(debug ? [devtoolsExchange, debugExchange] : []),
      offlineCache,

      retryExchange({
        initialDelayMs: 1000,
        maxDelayMs: 15000,
        maxNumberAttempts: 2,
        randomDelay: true,
        retryIf: (err) => {
          const isOffline = !currentNetworkOnlineStatus;

          // only retry if the error is a network error and the network is online
          // when offline, retry is trigger by the auth token refresh
          if (isOffline || !err?.networkError) {
            return false;
          }

          const { networkError } = err;
          // Aborted fetches (navigation, strict mode, superseded urql operations) must not
          // trigger automatic retries — that can look like a cancel → retry loop in DevTools.
          if (networkError.name === "AbortError") {
            return false;
          }

          if (isAuthSessionError(networkError)) {
            return false;
          }

          const message = networkError.message?.toLowerCase() ?? "";
          // Avoid matching generic "aborted" network strings (e.g. connection aborted) — only user/cancel aborts.
          if (
            message.includes("user aborted") ||
            message.includes("the operation was aborted") ||
            message.includes("signal is aborted")
          ) {
            return false;
          }

          return true;
        },
      }),
      authExchange(async () => {
        let authFailed = false;
        let token = null;

        if (blockedAuthError && isRefreshFailureCoolingDown()) {
          authFailed = true;
        } else {
          try {
            token = await getAccessTokenSilently();
            blockedAuthError = null;
            lastRefreshFailedAt = 0;
            lastRefreshError = null;
            resetReauthRequestCircuit();
          } catch (e) {
            const normalizedAuthError = normalizeAuthErrorText(e);
            const authRefreshError = createAuthRefreshError(e);
            if (isAuthSessionError(e)) {
              captureException(authRefreshError, {
                extra: {
                  authErrorSource: "bootstrap",
                  normalizedAuthError,
                },
              });
            }

            if (shouldForceLogoutForAuthError(e)) {
              authFailed = true;
              blockedAuthError = authRefreshError;
              lastRefreshFailedAt = Date.now();
              lastRefreshError = authRefreshError;
            }
          }
        }

        // If we couldn't get a token due to a non-transient error, request a
        // single reauth flow from the auth guard.
        if (authFailed) {
          requestReauthOnce("bootstrap");
        }

        const authState = {
          token,
        };

        return {
          addAuthToOperation(operation) {
            // the token isn't in the auth state, return the operation without changes
            if (!authState?.token) {
              return operation;
            }

            // fetchOptions can be a function (See Client API) but you can simplify this based of usage
            const fetchOptions =
              typeof operation.context.fetchOptions === "function"
                ? operation.context.fetchOptions()
                : operation.context.fetchOptions ?? {};

            return makeOperation(operation.kind, operation, {
              ...operation.context,
              fetchOptions: {
                ...fetchOptions,
                headers: {
                  ...fetchOptions.headers,
                  Authorization: `Bearer ${authState.token}`,
                },
              },
            });
          },

          didAuthError(error) {
            const isForbidden = error.graphQLErrors.some(
              (e) => e.extensions?.code === "FORBIDDEN"
            );
            return isForbidden;
          },

          async refreshAuth() {
            const now = Date.now();

            if (isRefreshFailureCoolingDown(now)) {
              requestReauthOnce("refreshAuth");
              throw (
                lastRefreshError ??
                new Error("Auth refresh blocked after previous failure")
              );
            }

            try {
              const newToken = await getAccessTokenSilently();
              authState.token = newToken;
              blockedAuthError = null;
              lastRefreshFailedAt = 0;
              lastRefreshError = null;
              resetReauthRequestCircuit();
            } catch (e) {
              const normalizedAuthError = normalizeAuthErrorText(e);
              const authRefreshError = createAuthRefreshError(e);
              if (isAuthSessionError(e)) {
                captureException(authRefreshError, {
                  extra: {
                    authErrorSource: "refreshAuth",
                    normalizedAuthError,
                  },
                });
              }

              if (shouldForceLogoutForAuthError(e)) {
                authState.token = null;
                blockedAuthError = authRefreshError;
                lastRefreshFailedAt = now;
                lastRefreshError = authRefreshError;
                requestReauthOnce("refreshAuth");
                throw authRefreshError;
              }
            }
          },

          willAuthError: () => {
            if (blockedAuthError) {
              return false;
            }

            const authToken = authState?.token;
            if (!authToken) {
              return true;
            }
            // Check token formating and expirancy
            try {
              const decodedToken = jwtDecode<JwtPayload>(authToken);
              if (decodedToken) {
                const expiry = decodedToken.exp ?? 0;
                // Check if the token is still valid for at least 10 seconds
                const now = (Date.now() + 10000) / 1000;
                const isExpired = Boolean(expiry < now);
                if (isExpired) {
                  return true;
                }
              }
            } catch (e) {
              return true;
            }
            return false;
          },
        };
      }),
      fetchExchange,
      // subscriptionExchange({
      //   forwardSubscription(operation) {
      //     return subscriptionClient.request(operation);
      //   },
      // }),
    ],
    fetch: async (input, init) => {
      const requestHeaders =
        input instanceof Request ? input.headers : undefined;
      const hasAuthorization =
        hasAuthorizationHeader(init?.headers) ||
        hasAuthorizationHeader(requestHeaders);

      if (blockedAuthError && !hasAuthorization) {
        throw blockedAuthError;
      }

      return fetch(input, init);
    },
    requestPolicy: "cache-and-network",
    url: env.VITE_HASURA_URL,
  });

  return client;
}

export function UrqlConfig(props: UrqlConfigProps) {
  const client = useClient();
  const [isReady, setIsReady] = React.useState(false);

  if (debug || DEBUG_URQL) {
    debugEvent(client);
  }
  React.useEffect(() => {
    const checkCacheRehydration = () => {
      if (navigator.onLine) {
        setIsReady(true);
        return;
      }
      // If we are offline, we wait for the cache to be loaded by urql Client
      if (typeof window !== "undefined" && window?.__hasCacheRehydrated) {
        setIsReady(true);
      } else {
        setTimeout(checkCacheRehydration, 50);
      }
    };

    checkCacheRehydration();
  }, []);

  if (client === null || !isReady) return <FullPageSpinner />;

  return <Provider value={client}>{props.children}</Provider>;
}

export const debugEvent = (client: Client) => {
  client?.subscribeToDebugTarget?.((event) => {
    console.log("urql:debug", event);
  });
};
